// TOTP Enrollment Modal — v0.7.x
//
// Real Supabase MFA enrollment flow:
//
//   1. cleanupUnverifiedFactors()   — clear any stuck factors from a
//      previously-abandoned enrollment so we don't trip
//      "factor already exists"
//   2. enrollTotp()                 — Supabase creates the factor in
//      'unverified' state and returns { id, totp: { qr_code, secret } }
//   3. user scans QR with Google Authenticator / 1Password / Authy
//   4. user enters the 6-digit code from the app
//   5. verifyTotp(factorId, code)   — Supabase activates the factor
//      ('verified') and the modal closes
//
// Note on recovery codes: Supabase TOTP does not natively issue
// recovery / backup codes. The previous version of this modal generated
// random strings and called them "recovery codes," which gave users
// false security. They've been removed. Real backup codes will land as a
// v0.7.x+ follow-on (separate table + RLS + a challenge-page fallback).

import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';

const modalOverlay = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalContent = {
  background: '#ffffff',
  borderRadius: '16px',
  padding: '32px',
  boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
  width: '100%',
  maxWidth: '480px',
  maxHeight: '90vh',
  overflow: 'auto',
};

const heading = {
  fontSize: '24px',
  fontWeight: 800,
  color: '#111827',
  margin: '0 0 8px',
};

const subtitle = {
  fontSize: '14px',
  color: '#6b7280',
  margin: '0 0 24px',
  lineHeight: 1.5,
};

const stepIndicator = {
  fontSize: '12px',
  fontWeight: 600,
  color: '#1d4ed8',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  margin: '0 0 16px',
};

const qrContainer = {
  display: 'flex',
  justifyContent: 'center',
  margin: '20px 0',
  padding: '20px',
  background: '#f9fafb',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
};

const codeInputStyle = {
  width: '100%',
  padding: '14px',
  borderRadius: '12px',
  border: '1px solid #d1d5db',
  fontSize: '20px',
  letterSpacing: '6px',
  textAlign: 'center',
  fontWeight: 700,
  boxSizing: 'border-box',
  fontFamily: 'monospace',
  marginBottom: '4px',
};

const labelStyle = {
  display: 'block',
  fontWeight: 700,
  marginBottom: '8px',
  fontSize: '14px',
  color: '#111827',
};

const instructionStyle = {
  fontSize: '13px',
  color: '#6b7280',
  margin: '12px 0 16px',
  lineHeight: 1.6,
};

const codeBlockStyle = {
  background: '#f3f4f6',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  padding: '12px',
  fontFamily: 'monospace',
  fontSize: '12px',
  color: '#111827',
  wordBreak: 'break-all',
  margin: '10px 0',
  lineHeight: 1.8,
};

const buttonRow = {
  display: 'flex',
  gap: '12px',
  marginTop: '24px',
};

const primaryButton = (enabled) => ({
  flex: 1,
  background: enabled ? '#1d4ed8' : '#9ca3af',
  color: '#ffffff',
  border: 'none',
  borderRadius: '12px',
  padding: '12px',
  fontSize: '15px',
  fontWeight: 700,
  cursor: enabled ? 'pointer' : 'not-allowed',
});

const secondaryButton = {
  flex: 1,
  background: '#f3f4f6',
  color: '#111827',
  border: '1px solid #d1d5db',
  borderRadius: '12px',
  padding: '12px',
  fontSize: '15px',
  fontWeight: 700,
  cursor: 'pointer',
};

const errorBox = {
  background: '#fff5f5',
  border: '1px solid #ef4444',
  borderRadius: '8px',
  padding: '12px',
  color: '#b91c1c',
  fontSize: '13px',
  marginBottom: '16px',
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
};

const successBox = {
  background: '#f0fdf4',
  border: '1px solid #22c55e',
  borderRadius: '8px',
  padding: '14px',
  color: '#15803d',
  fontSize: '14px',
  marginBottom: '16px',
  lineHeight: 1.5,
};

const infoBox = {
  background: '#eff6ff',
  border: '1px solid #bfdbfe',
  borderRadius: '8px',
  padding: '12px',
  color: '#1e40af',
  fontSize: '13px',
  lineHeight: 1.6,
};

function QrCodeDisplay({ qrCodeUrl }) {
  if (!qrCodeUrl) return null;
  return (
    <div style={qrContainer}>
      <img src={qrCodeUrl} alt="TOTP QR Code" style={{ width: '200px', height: '200px' }} />
    </div>
  );
}

export default function TotpEnrollmentModal({ onClose, onEnrollmentComplete }) {
  const {
    enrollTotp,
    verifyTotp,
    cleanupUnverifiedFactors,
    generateRecoveryCodes,
    user,
  } = useAuth();

  // 'loading' | 'scan' | 'verify' | 'codes' | 'done' | 'error'
  const [step, setStep] = useState('loading');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [factorId, setFactorId] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [recoveryCodesError, setRecoveryCodesError] = useState('');

  // ──────────────────────────────────────────────────────────────────
  // Mount-lifecycle refs — these survive React 18 StrictMode's
  // intentional double-mount in dev.
  //
  //   startedRef → "did we already kick off enrollment?"
  //                Prevents the second mount from making a duplicate
  //                Supabase enroll call that would collide with the
  //                first one on friendly_name (mfa_factor_name_conflict).
  //
  //   aliveRef   → "is the modal still actually on screen?"
  //                We can't use a closure-local `isMounted` here,
  //                because StrictMode's fake unmount runs the cleanup
  //                between mount #1 and mount #2 and would flip it
  //                to false — then the first enroll's resolve handler
  //                would bail and the modal would hang on "Preparing".
  //                A ref on the component instance survives that, and
  //                only flips false on a REAL close (handleCancel
  //                / handleDone via the onClose handlers below).
  // ──────────────────────────────────────────────────────────────────
  const startedRef = useRef(false);
  const aliveRef = useRef(true);

  // Start enrollment exactly once per modal open.
  useEffect(() => {
    if (startedRef.current) return undefined;
    startedRef.current = true;
    aliveRef.current = true;

    let timeoutId;

    async function startEnrollment() {
      setError('');

      // Sweep up any unverified TOTP factor left over from a prior
      // attempt. Belt-and-suspenders alongside the unique friendlyName
      // below — covers cases where the user has aal2 (so unenroll
      // works) and lets enrollment proceed cleanly.
      await cleanupUnverifiedFactors();
      if (!aliveRef.current) return;

      // 10-second guard so a hung network call doesn't leave the modal
      // spinning forever.
      timeoutId = setTimeout(() => {
        if (!aliveRef.current) return;
        setError(
          'Enrollment is taking too long. Your session may have expired. Close this modal, sign out, sign back in, and try again.'
        );
        setStep('error');
      }, 10000);

      // Generate a unique friendly_name for this enrollment so a stale
      // factor (one the cleanup pass couldn't see/remove at aal1) can't
      // collide on friendly_name="" and break us.
      const friendlyName = `Authenticator app — ${new Date().toLocaleString()}`;

      const { data, error: enrollError } = await enrollTotp(friendlyName);
      clearTimeout(timeoutId);

      if (!aliveRef.current) return;

      if (enrollError) {
        // Surface the raw Supabase error so we can see exactly what's
        // blocking enrollment (different Supabase versions phrase the
        // "factor already exists" case differently — message, code, and
        // status all help).
        console.error('TOTP enroll failed:', enrollError);
        const rawDetail =
          [enrollError.code, enrollError.status, enrollError.message]
            .filter(Boolean)
            .join(' · ') || 'Unknown error';

        const looksLikeStuckFactor =
          /(already|exists|enrolled|conflict|one (totp|factor))/i.test(
            enrollError.message || ''
          ) ||
          enrollError.code === 'mfa_factor_name_conflict' ||
          enrollError.code === 'too_many_enrolled_mfa_factors';

        if (looksLikeStuckFactor) {
          setError(
            `A previous authenticator factor is still attached to this account and the in-browser cleanup couldn't remove it. Close this modal and tap "Reset 2FA" in Account → Security to clear it server-side, then try again.\n\nSupabase said: ${rawDetail}`
          );
        } else {
          setError(`Unable to start enrollment.\n\nSupabase said: ${rawDetail}`);
        }
        setStep('error');
        return;
      }

      if (data?.totp && data?.id) {
        setQrCodeUrl(data.totp.qr_code);
        setSecret(data.totp.secret);
        setFactorId(data.id);
        setStep('scan');
      } else {
        setError('Supabase returned an unexpected enrollment response.');
        setStep('error');
      }
    }

    startEnrollment();

    // Cleanup only clears the timeout. We deliberately DO NOT flip
    // aliveRef here, because StrictMode's fake unmount runs this
    // cleanup and we want the in-flight enrollment to finish updating
    // state on mount #2. The real-close handlers (handleCancel,
    // handleDone) flip aliveRef.
    return () => {
      clearTimeout(timeoutId);
    };
  }, []); // eslint-disable-line

  async function handleVerify(e) {
    if (e && e.preventDefault) e.preventDefault();

    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code shown in your authenticator app.');
      return;
    }
    if (!factorId) {
      setError('Missing factor id — close this modal and try again.');
      return;
    }

    setIsVerifying(true);
    setError('');

    const { error: verifyError } = await verifyTotp(factorId, code);

    if (verifyError) {
      setIsVerifying(false);
      setError(
        verifyError.message ||
          'That code did not match. Try the current code from your authenticator app (codes refresh every 30 seconds).'
      );
      return;
    }

    // Factor is verified. Now issue a fresh batch of recovery codes
    // and show them to the user before letting them close the modal.
    // If code generation fails we still want them to know enrollment
    // succeeded — surface the error inline on the codes step so they
    // can retry from Account → Security without losing their 2FA setup.
    const { data: codesData, error: codesError } =
      await generateRecoveryCodes();

    setIsVerifying(false);

    if (codesError) {
      setRecoveryCodesError(
        codesError.message ||
          'Two-factor authentication is enabled, but we could not generate recovery codes. You can issue codes from Account → Security.'
      );
      setRecoveryCodes([]);
    } else {
      setRecoveryCodesError('');
      setRecoveryCodes(codesData?.codes || []);
    }

    setStep('codes');
  }

  // ---- Recovery-code helpers ----

  function copyRecoveryCodes() {
    const text = recoveryCodes.join('\n');
    navigator.clipboard
      .writeText(text)
      .then(() => {
        // small inline confirmation via the error/notice slot
        setRecoveryCodesError('');
      })
      .catch(() => {
        setRecoveryCodesError(
          'Could not copy automatically — select the codes manually and copy them.'
        );
      });
  }

  function downloadRecoveryCodes() {
    const header = `Rentava — MFA recovery codes
Account: ${user?.email || 'unknown'}
Generated: ${new Date().toLocaleString()}

Each code can be used ONCE. Using a code will deactivate your
authenticator app — you'll need to set up a new one afterwards.

`;
    const blob = new Blob([header + recoveryCodes.join('\n') + '\n'], {
      type: 'text/plain',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rentava-recovery-codes-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function handleDone() {
    aliveRef.current = false;
    onEnrollmentComplete && onEnrollmentComplete();
    onClose();
  }

  async function handleCancel() {
    // Flip aliveRef immediately so any in-flight enroll response can't
    // race back and stomp on state after the modal is gone.
    aliveRef.current = false;
    // If we created an unverified factor and the user bails, clean it
    // up so the next enrollment attempt starts from a clean slate.
    if (factorId && step !== 'done') {
      await cleanupUnverifiedFactors();
    }
    onClose();
  }

  return (
    <div style={modalOverlay} onClick={handleCancel}>
      <div style={modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Loading */}
        {step === 'loading' && (
          <>
            <p style={{ color: '#6b7280', textAlign: 'center', margin: '20px 0' }}>
              Preparing enrollment…
            </p>
          </>
        )}

        {/* Step 1: Scan QR */}
        {step === 'scan' && (
          <>
            <div style={stepIndicator}>Step 1 of 2</div>
            <h2 style={heading}>Scan with your authenticator</h2>
            <p style={subtitle}>
              Open Google Authenticator, 1Password, Authy, or another authenticator app and scan
              this QR code to add the account.
            </p>

            <QrCodeDisplay qrCodeUrl={qrCodeUrl} />

            <details style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#111827' }}>
                Can't scan? Enter this code manually
              </summary>
              <div style={codeBlockStyle}>{secret}</div>
            </details>

            <div style={infoBox}>
              Once you've added the account, you'll see a 6-digit code that refreshes every 30
              seconds. We'll verify it on the next screen.
            </div>

            {error && <div style={errorBox}>{error}</div>}

            <div style={buttonRow}>
              <button style={secondaryButton} onClick={handleCancel}>
                Cancel
              </button>
              <button
                style={primaryButton(true)}
                onClick={() => {
                  setError('');
                  setCode('');
                  setStep('verify');
                }}
              >
                Next: Enter Code
              </button>
            </div>
          </>
        )}

        {/* Step 2: Verify code */}
        {step === 'verify' && (
          <>
            <div style={stepIndicator}>Step 2 of 2</div>
            <h2 style={heading}>Enter the 6-digit code</h2>
            <p style={subtitle}>
              Type the current code from your authenticator app to finish setup. Codes change every
              30 seconds — if it's about to flip, wait for the next one.
            </p>

            <form onSubmit={handleVerify}>
              <label htmlFor="totp-enroll-code" style={labelStyle}>
                Authenticator code
              </label>
              <input
                id="totp-enroll-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength="6"
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                style={codeInputStyle}
                disabled={isVerifying}
                autoFocus
                autoComplete="one-time-code"
              />
              <p style={instructionStyle}>
                Make sure the code matches what's shown in your authenticator app right now.
              </p>

              {error && <div style={errorBox}>{error}</div>}

              <div style={buttonRow}>
                <button
                  type="button"
                  style={secondaryButton}
                  onClick={() => {
                    setError('');
                    setStep('scan');
                  }}
                >
                  Back
                </button>
                <button
                  type="submit"
                  style={primaryButton(code.length === 6 && !isVerifying)}
                  disabled={code.length !== 6 || isVerifying}
                >
                  {isVerifying ? 'Verifying…' : 'Verify and finish'}
                </button>
              </div>
            </form>
          </>
        )}

        {/* Codes — show the freshly-generated recovery codes ONCE */}
        {step === 'codes' && (
          <>
            <div style={stepIndicator}>Save these now</div>
            <h2 style={heading}>Your recovery codes</h2>
            <p style={subtitle}>
              You're enrolled — but if you ever lose access to your authenticator app, you'll
              need one of these codes to get back in. Save them somewhere safe.
            </p>

            {recoveryCodes.length > 0 ? (
              <>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '8px',
                    padding: '16px',
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    marginBottom: '14px',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    color: '#111827',
                  }}
                >
                  {recoveryCodes.map((rc) => (
                    <div key={rc} style={{ textAlign: 'center', padding: '6px 4px' }}>
                      {rc}
                    </div>
                  ))}
                </div>

                <div style={buttonRow}>
                  <button type="button" style={secondaryButton} onClick={copyRecoveryCodes}>
                    Copy
                  </button>
                  <button
                    type="button"
                    style={secondaryButton}
                    onClick={downloadRecoveryCodes}
                  >
                    Download .txt
                  </button>
                </div>

                <div style={{ ...infoBox, marginTop: '16px' }}>
                  <strong>Each code works once.</strong> Using one will deactivate your current
                  authenticator app — you'll need to set up a new one afterwards. You can
                  regenerate this list any time from Account → Security.
                </div>
              </>
            ) : (
              <div style={errorBox}>
                {recoveryCodesError ||
                  'No recovery codes were returned. Generate them from Account → Security.'}
              </div>
            )}

            {recoveryCodesError && recoveryCodes.length > 0 && (
              <div style={{ ...errorBox, marginTop: '12px' }}>{recoveryCodesError}</div>
            )}

            <div style={{ ...buttonRow, marginTop: '20px' }}>
              <button style={primaryButton(true)} onClick={() => setStep('done')}>
                I've saved my codes
              </button>
            </div>
          </>
        )}

        {/* Done */}
        {step === 'done' && (
          <>
            <h2 style={heading}>Two-factor authentication is on</h2>
            <p style={subtitle}>
              Your authenticator app is now active on this account. The next time you sign in,
              you'll be asked for a 6-digit code after your password.
            </p>

            <div style={successBox}>
              ✓ Authenticator enrolled and verified. Your account is now protected by 2FA.
            </div>

            <div style={infoBox}>
              Recovery codes have been issued. If you lose your authenticator, use one of them
              from the sign-in challenge page to deactivate the factor and re-enroll.
            </div>

            <div style={buttonRow}>
              <button style={primaryButton(true)} onClick={handleDone}>
                Done
              </button>
            </div>
          </>
        )}

        {/* Error */}
        {step === 'error' && (
          <>
            <h2 style={heading}>Enrollment failed</h2>
            {error && <div style={errorBox}>{error}</div>}
            <div style={buttonRow}>
              <button
                style={secondaryButton}
                onClick={() => {
                  aliveRef.current = false;
                  onClose();
                }}
              >
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
